import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import type { CreativeTemplateExample, CreativeTemplateExampleWritePayload } from '../types/creative-template-example.types';
import { CANVAS_CLASS_OPTIONS } from '../types/creative-template.types';
import {
  applyBrandBindings,
  buildCreativeTemplateExamplePayload,
  createDefaultLayer,
  DEMO_BRANDS,
  exampleIdFor,
  validateCreativeTemplateExampleForm,
} from '../helpers/creative-template-example.helpers';
import { BrandBindingsEditor, SceneGraphEditor } from './creative-template-example-editors';
import { SceneGraphPreview } from './scene-graph-preview';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

interface CreativeTemplateExampleFormProps {
  initialExample?: CreativeTemplateExample | null;
  isCreate?: boolean;
  isSaving?: boolean;
  submitError?: string | null;
  profileOptions?: string[];
  onSubmit: (values: CreativeTemplateExampleWritePayload) => void;
  onCancel?: () => void;
}

export function CreativeTemplateExampleForm({
  initialExample,
  isCreate = false,
  isSaving = false,
  submitError = null,
  profileOptions = [],
  onSubmit,
  onCancel,
}: CreativeTemplateExampleFormProps) {
  const payload = initialExample?.payload;
  const [exampleId, setExampleId] = useState(initialExample?.example_id ?? '');
  const [profileId, setProfileId] = useState(initialExample?.profile_id ?? '');
  const [sizeKey, setSizeKey] = useState(initialExample?.size_key ?? 'panel_square');
  const [sceneGraph, setSceneGraph] = useState(payload?.scene_graph ?? {
    canvas_class: 'panel_square' as const,
    layers: [
      createDefaultLayer('background'),
      createDefaultLayer('headline'),
      createDefaultLayer('logo'),
    ],
  });
  const [brandBindings, setBrandBindings] = useState(payload?.brand_bindings ?? []);
  const [thumbnailUrl, setThumbnailUrl] = useState(payload?.thumbnail_url ?? '');
  const [demoBrandSlug, setDemoBrandSlug] = useState(payload?.canonical_demo_brand_slug ?? 'demo-qsr');
  const [changeNote, setChangeNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const previewSceneGraph = useMemo(
    () => applyBrandBindings(sceneGraph, brandBindings, demoBrandSlug),
    [sceneGraph, brandBindings, demoBrandSlug],
  );

  const handleProfileOrSizeChange = (nextProfileId: string, nextSizeKey: typeof sizeKey) => {
    setProfileId(nextProfileId);
    setSizeKey(nextSizeKey);
    if (isCreate) {
      setExampleId(exampleIdFor(nextProfileId, nextSizeKey));
      setSceneGraph((current) => ({ ...current, canvas_class: nextSizeKey }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const values = buildCreativeTemplateExamplePayload({
      ...(isCreate ? { example_id: exampleId, profile_id: profileId, size_key: sizeKey } : {}),
      scene_graph: sceneGraph,
      brand_bindings: brandBindings,
      thumbnail_url: thumbnailUrl,
      canonical_demo_brand_slug: demoBrandSlug,
      change_note: changeNote,
    });
    const error = validateCreativeTemplateExampleForm(values, { isCreate });
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    onSubmit(values);
  };

  const displayError = validationError ?? submitError;

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {displayError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {displayError}
        </p>
      ) : null}

      {isCreate ? (
        <div className="grid gap-4 md:grid-cols-2">
          <label className="relative block space-y-1">
            <span className={labelClassName}>Profile</span>
            <select
              value={profileId}
              onChange={(event) => handleProfileOrSizeChange(event.target.value, sizeKey)}
              className={selectClassName}
            >
              <option value="">Select profile…</option>
              {profileOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <label className="relative block space-y-1">
            <span className={labelClassName}>Size key</span>
            <select
              value={sizeKey}
              onChange={(event) => handleProfileOrSizeChange(profileId, event.target.value as typeof sizeKey)}
              className={selectClassName}
            >
              {CANVAS_CLASS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 md:col-span-2">
            <span className={labelClassName}>Example ID</span>
            <Input value={exampleId} onChange={(event) => setExampleId(event.target.value)} />
          </label>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <SceneGraphEditor sceneGraph={sceneGraph} onChange={setSceneGraph} />
          <BrandBindingsEditor bindings={brandBindings} onChange={setBrandBindings} />
        </div>
        <div className="space-y-4">
          <label className="relative block space-y-1">
            <span className={labelClassName}>Canonical demo brand</span>
            <select
              value={demoBrandSlug}
              onChange={(event) => setDemoBrandSlug(event.target.value)}
              className={selectClassName}
            >
              {Object.keys(DEMO_BRANDS).map((slug) => (
                <option key={slug} value={slug}>{slug}</option>
              ))}
            </select>
          </label>
          <SceneGraphPreview sceneGraph={previewSceneGraph} />
          <label className="block space-y-1">
            <span className={labelClassName}>Thumbnail URL</span>
            <Input value={thumbnailUrl} onChange={(event) => setThumbnailUrl(event.target.value)} />
          </label>
        </div>
      </div>

      {!isCreate ? (
        <label className="block space-y-1">
          <span className={labelClassName}>Change note</span>
          <Input value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="Describe what changed in this version" />
        </label>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <SchedulerDsButton type="submit" disabled={isSaving}>
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
          {isCreate ? 'Create example' : 'Save new version'}
        </SchedulerDsButton>
        {onCancel ? (
          <SchedulerDsButton type="button" variant="secondary" disabled={isSaving} onClick={onCancel}>
            Cancel
          </SchedulerDsButton>
        ) : null}
      </div>
    </form>
  );
}
